import { isActive, PIPELINE_STATUS_LABELS, type PipelineStatus } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Bell,
  ClipboardCheck,
  Database,
  FileSpreadsheet,
  FileUp,
  Globe,
  type LucideIcon,
  MessageSquareWarning,
  ScrollText,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback } from 'react';
import { ProgressCard } from '../components/activity/progress-card';
import { DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { type TourDef, useScreenTour } from '../components/guidance/tour';
import { GettingStartedChecklist } from '../components/help/checklist';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { DropZone } from '../components/upload/drop-zone';
import { uploadsQuery } from '../components/upload/recent-uploads';
import { useActivity } from '../lib/activity';
import { api } from '../lib/api';
import { useMySchedules } from '../lib/reports';
import { useReviewQueue } from '../lib/review';
import { useAutoMatchCount, useUnreviewedUploads } from '../lib/review-inbox';
import { useInboxPairs } from '../lib/same-person';
import { useCan } from '../lib/session';
import type { PickedFile } from '../lib/uploads/collect';
import { handOffFiles } from '../lib/uploads/handoff';

export const Route = createFileRoute('/')({ component: Home });

const HOME_TOUR: TourDef = {
  id: 'home',
  steps: [
    { title: 'Welcome to Hoopoe', body: 'A quick look around. You can reopen this tour any time from the ? menu.' },
    {
      target: '[data-tour="home-needs-you"]',
      title: 'What needs you',
      body: 'Files to review, facts added without review, questions about names and new report matches. Each card opens the place to deal with it.',
    },
    {
      target: 'nav[aria-label="Main"]',
      title: 'Screens',
      body: 'Upload sources, review what the AI found, then explore actors in the directory, graph, timeline and map.',
    },
    {
      target: '[data-tour="search"]',
      title: 'Search everything',
      body: 'Press ⌘K to find actors in any script, sources and help articles.',
    },
    {
      target: '[aria-label="Help"]',
      title: 'Help is always here',
      body: 'The ? menu opens the article for the screen you are on, this tour and keyboard shortcuts.',
      learnMore: 'core-concepts',
    },
  ],
};

/** Uploads shown on Home; the full list is on the Upload screen. */
const HOME_UPLOADS = 5;
/** Running jobs shown on Home; the rest are on the Activity screen. */
const HOME_JOBS = 3;

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

interface NeedCard {
  key: string;
  icon: LucideIcon;
  count: number;
  label: string;
  detail: string;
  to: string;
}

function Home() {
  useScreenTour(HOME_TOUR);
  const canWrite = useCan('content.write');
  const health = useQuery({
    queryKey: ['health'],
    queryFn: async () => (await fetch('/api/health')).json() as Promise<{ ok: boolean; env: string }>,
  });
  const uploads = useQuery({ ...uploadsQuery, enabled: canWrite, refetchInterval: 15_000 });
  const hasUploads = (uploads.data?.uploads.length ?? 0) > 0;
  return (
    <div className="space-y-8">
      <PageHeader title="Home" description="What needs you, what's running, and where to add new material." />
      <GettingStartedChecklist hideWhenDone />
      <NeedsYou />
      {canWrite && <AddMaterial />}
      <InProgress />
      {canWrite && uploads.isSuccess && !hasUploads && (
        <EmptyState icon={FileUp} title="No uploads yet" learnMore="upload-a-batch">
          Drop files or whole folders above, or click Upload. Hoopoe reads them, adds them to the Library and suggests actors, relations and
          events for review.
        </EmptyState>
      )}
      <Tip id="home-review-later" learnMore="review-statuses">
        Uploads you skip reviewing still reach the graph, marked <em>unreviewed</em>. Nothing is presented as verified until a person checks
        it.
      </Tip>
      <p data-testid="api-status" className="text-xs text-muted-foreground">
        {health.data?.ok ? `API ok (${health.data.env})` : 'Connecting…'}
      </p>
    </div>
  );
}

/** Counts of what a person still has to do, each a link to the screen where it is done. Zero counts are left out. */
function NeedsYou() {
  const canWrite = useCan('content.write');
  const admin = useCan('admin.manage');
  const { notifications } = useActivity();
  const queue = useReviewQueue({}, canWrite);
  const unreviewed = useUnreviewedUploads({}, canWrite);
  const auto = useAutoMatchCount(canWrite);
  const pairs = useInboxPairs(canWrite);
  const schedules = useMySchedules();
  const uploads = useQuery({ ...uploadsQuery, enabled: canWrite });
  const requests = useQuery({
    queryKey: ['admin', 'requests'],
    queryFn: () => api<{ requests: { status: string }[] }>('/admin/access-requests'),
    enabled: admin,
  });

  const skipped = unreviewed.data?.documents ?? [];
  const facts = skipped.reduce((n, d) => n + d.unreviewed, 0);
  const alerts = (schedules.data?.schedules ?? []).filter(s => (s.lastNewCount ?? 0) > 0);
  const answers = (uploads.data?.uploads ?? []).filter(u => u.status === 'awaiting_input');
  const failed = (uploads.data?.uploads ?? []).filter(u => u.status === 'failed');

  const cards: NeedCard[] = [
    ...(canWrite
      ? [
          {
            key: 'answers',
            icon: MessageSquareWarning,
            count: answers.length,
            label: plural(answers.length, 'file waits', 'files wait') + ' for your answer',
            detail: 'A marking was found: say whether it is confidential. Nothing goes to an external model until then.',
            to: '/upload',
          },
          {
            key: 'failed',
            icon: MessageSquareWarning,
            count: failed.length,
            label: `${plural(failed.length, 'upload')} failed`,
            detail: 'Open the upload to see why and retry.',
            to: '/upload',
          },
          {
            key: 'queue',
            icon: ClipboardCheck,
            count: queue.data?.documents.length ?? 0,
            label: `${plural(queue.data?.documents.length ?? 0, 'file')} waiting for review`,
            detail: 'Entities, then facts and relations, then the AI re-check.',
            to: '/review',
          },
          {
            key: 'unreviewed',
            icon: ClipboardCheck,
            count: facts,
            label: `${plural(facts, 'fact')} added without review`,
            detail: `From ${plural(skipped.length, 'upload')}. Verify or reject each against its quote.`,
            to: '/review',
          },
          {
            key: 'auto',
            icon: Users,
            count: auto.data?.entities ?? 0,
            label: `${plural(auto.data?.entities ?? 0, 'automatic match', 'automatic matches')} to confirm`,
            detail: 'Names attached to an existing record because they matched closely.',
            to: '/review',
          },
          {
            key: 'pairs',
            icon: Users,
            count: pairs.data?.open.length ?? 0,
            label: `${plural(pairs.data?.open.length ?? 0, '“Same person?” question')}`,
            detail: 'One person or organization possibly written in two scripts or languages.',
            to: '/review',
          },
        ]
      : []),
    {
      key: 'alerts',
      icon: Sparkles,
      count: alerts.length,
      label: `${plural(alerts.length, 'followed report')} with new matches`,
      detail: alerts
        .slice(0, 3)
        .map(s => plural(s.lastNewCount ?? 0, 'new match', 'new matches'))
        .join(' · '),
      to: '/reports',
    },
    {
      key: 'notifications',
      icon: Bell,
      count: notifications.filter(n => !n.readAt).length,
      label: `${plural(notifications.filter(n => !n.readAt).length, 'unread notification')}`,
      detail: 'Open the bell at the top of the screen.',
      to: '/activity',
    },
    ...(admin
      ? [
          {
            key: 'requests',
            icon: UserPlus,
            count: (requests.data?.requests ?? []).filter(r => r.status === 'pending').length,
            label: `${plural((requests.data?.requests ?? []).filter(r => r.status === 'pending').length, 'access request')} to decide`,
            detail: 'Approve with a role, or reject.',
            to: '/admin/users',
          },
        ]
      : []),
  ];
  const open = cards.filter(c => c.count > 0);
  const loading = canWrite && (queue.isPending || unreviewed.isPending);

  return (
    <section aria-labelledby="home-needs-you" data-tour="home-needs-you" className="space-y-3">
      <h2 id="home-needs-you" className="text-lg font-semibold">
        Needs you
      </h2>
      {loading ? (
        <p className="text-sm text-muted-foreground">Checking…</p>
      ) : open.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing needs you right now.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {open.map(c => (
            <li key={c.key}>
              <Link
                to={c.to}
                className="flex h-full gap-3 rounded-lg border bg-surface p-4 transition-colors hover:bg-muted"
                data-testid={`need-${c.key}`}
              >
                <c.icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="space-y-1">
                  <span className="block font-medium">{c.label}</span>
                  {c.detail && <span className="block text-sm text-muted-foreground">{c.detail}</span>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Drop files here to start an upload on the Upload screen, plus the other ways material comes in. */
function AddMaterial() {
  const admin = useCan('admin.manage');
  const navigate = useNavigate();
  const onFiles = useCallback(
    (files: PickedFile[]) => {
      if (!files.length) return;
      handOffFiles(files);
      void navigate({ to: '/upload' });
    },
    [navigate],
  );
  const ways: { to: string; icon: LucideIcon; label: string }[] = [
    { to: '/datasets', icon: Database, label: 'Import a dataset' },
    { to: '/sheets', icon: FileSpreadsheet, label: 'Import a profile sheet' },
    { to: '/entity/new', icon: UserPlus, label: 'Add an entity by hand' },
    // Report v2: describe a report in words; it is planned, written and illustrated.
    { to: '/reports/new', icon: ScrollText, label: 'New report' },
    ...(admin ? [{ to: '/admin/scrapers', icon: Globe, label: 'Monitor a website' }] : []),
  ];
  return (
    <section aria-labelledby="home-add" className="space-y-3">
      <h2 id="home-add" className="text-lg font-semibold">
        Add material
      </h2>
      <DropZone onFiles={onFiles} />
      <ul className="flex flex-wrap gap-2">
        {ways.map(w => (
          <li key={w.to}>
            <Button asChild variant="outline" size="sm">
              <Link to={w.to}>
                <w.icon aria-hidden="true" /> {w.label}
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Jobs running now (live), and the person's newest uploads with where each one stands. */
function InProgress() {
  const canWrite = useCan('content.write');
  const { jobs, now, cancel } = useActivity();
  const uploads = useQuery({ ...uploadsQuery, enabled: canWrite });
  const running = jobs.filter(isActive);
  const newest = (uploads.data?.uploads ?? []).slice(0, HOME_UPLOADS);
  if (!running.length && !newest.length) return null;
  return (
    <section aria-labelledby="home-progress" className="space-y-3">
      <h2 id="home-progress" className="text-lg font-semibold">
        In progress
      </h2>
      {running.slice(0, HOME_JOBS).map(j => (
        <ProgressCard key={j.id} job={j} now={now} onCancel={() => cancel(j.id)} />
      ))}
      {running.length > HOME_JOBS && (
        <Link to="/activity" className="text-sm underline underline-offset-2">
          {plural(running.length - HOME_JOBS, 'more job')} running: see Activity
        </Link>
      )}
      {newest.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Your newest uploads</h3>
          <ul className="divide-y rounded-lg border bg-surface text-sm">
            {newest.map(u => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-status={u.status}>
                <DataText className="min-w-0 truncate font-medium">{u.filename}</DataText>
                <span className="flex items-center gap-3 text-xs text-muted-foreground">
                  {PIPELINE_STATUS_LABELS[u.status as PipelineStatus] ?? u.status}
                  {(u.status === 'in_review' || u.status === 'extracted') && (
                    <Link to="/review/$id" params={{ id: u.id }} className="text-foreground underline underline-offset-2">
                      Review
                    </Link>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/upload" className="text-sm underline underline-offset-2">
            All your uploads
          </Link>
        </div>
      )}
    </section>
  );
}
